import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import config from "../../Config";
import FeeSystemMenu from "./FeeSystemMenu";
import { toast, ToastContainer } from "react-toastify";
import Spinner from "react-bootstrap/Spinner";
import { scrollToTop } from "../Shared/ScrollOnTop";
import moment from "moment";
import MonthsDropDown from "../Shared/MonthsDropDown";
import YearsDropDown from "../Shared/YearsDropDown";

function ScrollDifferenceDetail() {
  const { studentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [filterMonth, setFilterMonth] = useState(moment().format("M"));
  const [filterYear, setFilterYear] = useState(moment().format("YYYY"));

  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const MemberType = localStorage.getItem("MemberType");

  useEffect(() => {
    scrollToTop();
    document.title = "Scroll entry vs ledger — detail";
  }, []);

  useEffect(() => {
    if (!studentId) return;

    setLoading(true);
    const m = parseInt(filterMonth, 10);
    const y = parseInt(filterYear, 10);
    const payload = {
      AccessKey,
      UserID,
      MemberType: MemberType || "Admin",
      Student_ID: parseInt(studentId, 10),
      SelectedMonth: m >= 1 && m <= 12 ? m : parseInt(moment().format("M"), 10),
      FYear: y > 1990 ? y : parseInt(moment().format("YYYY"), 10),
    };

    axios({
      method: "post",
      url: config.base_url + "FeeSystem/GetScrollDifferenceDetail",
      headers: { "Content-Type": "application/json" },
      data: payload,
    })
      .then((res) => {
        if (res.data.status_code === 1) {
          setDetail(res.data);
        } else {
          toast.error(res.data.status_message || "Could not load detail", {
            position: toast.POSITION.TOP_RIGHT,
          });
          setDetail(null);
        }
        setLoading(false);
      })
      .catch(() => {
        toast.error("Request failed", { position: toast.POSITION.TOP_RIGHT });
        setLoading(false);
      });
  }, [studentId, AccessKey, UserID, MemberType, filterMonth, filterYear]);

  const ledgerMismatch = (row) =>
    row.receiveableFeeMismatch === true || row.ReceiveableFeeMismatch === true;

  const sum = detail?.Summary;

  return (
    <>
      <FeeSystemMenu />
      <ToastContainer />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/Dashboard">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/FeeSystem/FeeSystemDashboard">Fee System</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Scroll difference detail
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Scroll entry vs ledger (student {studentId})</div>
              </div>
              <div className="col-md-4 text-md-end">
                <Link
                  to="/FeeSystem/FeeSystemDashboard"
                  className="button button-white button-icon"
                >
                  Back to fee dashboard
                  <i className="ri-arrow-left-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            <div className="card mb-3">
              <div className="card-body py-3">
                <div className="row g-3 align-items-end">
                  <div className="col-auto">
                    <label className="form-label small mb-0">Month</label>
                    <select
                      className="form-select"
                      style={{ minWidth: "150px" }}
                      value={filterMonth}
                      onChange={(e) => setFilterMonth(e.target.value)}
                    >
                      <MonthsDropDown />
                    </select>
                  </div>
                  <div className="col-auto">
                    <label className="form-label small mb-0">Year</label>
                    <select
                      className="form-select"
                      style={{ minWidth: "110px" }}
                      value={filterYear}
                      onChange={(e) => setFilterYear(e.target.value)}
                    >
                      <YearsDropDown />
                    </select>
                  </div>
                  <div className="col">
                    <p className="text-muted small mb-0">
                      Scroll lines: <code>Dated</code> in this month. Ledger lines: <code>PostingDate</code> in this month.
                      Challans: <code>Month</code>/<code>Year</code> match this period. Rows where{" "}
                      <code>ReceiveableFee</code> ≠ <code>ReceivedAmount</code> in the ledger grid are highlighted.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : !detail ? (
              <p className="text-muted">No data.</p>
            ) : (
              <>
                <div className="card mb-3">
                  <div className="card-body">
                    <h4 className="card-title mb-3">Student</h4>
                    <p className="mb-1">
                      <strong>Registration:</strong> {detail.Registration_No || "—"}
                    </p>
                    <p className="mb-0">
                      <strong>Name:</strong> {detail.Student_Name || "—"}
                    </p>
                  </div>
                </div>

                <div className="alert alert-secondary" role="note">
                  <strong>
                    Period: {detail.FilterMonth ?? filterMonth}/{detail.FilterYear ?? filterYear}
                  </strong>
                  <p className="mb-0 small mt-2">{detail.Explanation}</p>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="text-muted">Bank scroll (posted to ledger)</h6>
                        <p className="h4 mb-0">
                          Rs.{" "}
                          {sum?.SumScrollDeposited_PostedToLedger != null
                            ? Number(sum.SumScrollDeposited_PostedToLedger).toFixed(2)
                            : "0.00"}
                        </p>
                        <small className="text-muted">
                          Sum of <code>Amount_Deposited</code> where{" "}
                          <code>Posted_In_Ledger = 1</code> and <code>Dated</code> in selected month
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="text-muted">Ledger received</h6>
                        <p className="h4 mb-0">
                          Rs.{" "}
                          {sum?.SumLedgerReceivedAmount != null
                            ? Number(sum.SumLedgerReceivedAmount).toFixed(2)
                            : "0.00"}
                        </p>
                        <small className="text-muted">
                          Sum of <code>ReceivedAmount</code> where <code>PostingDate</code> in selected month
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card h-100 border-primary">
                      <div className="card-body">
                        <h6 className="text-muted">Difference</h6>
                        <p className="h4 mb-0 text-primary">
                          Rs.{" "}
                          {sum?.Difference != null
                            ? Number(sum.Difference).toFixed(2)
                            : "0.00"}
                        </p>
                        <small className="text-muted">Scroll − ledger</small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card mb-3">
                  <div className="card-body">
                    <h4 className="card-title mb-3">Bank scroll entries</h4>
                    <p className="small text-muted mb-3">
                      <code>tblScrollEntry</code> filtered by <strong>Dated</strong> in the selected month. Rows with{" "}
                      <strong>Posted to ledger = Yes</strong> contribute to the summary total.
                    </p>
                    <div className="table-responsive">
                      <table className="table table-theme table-sm">
                        <thead className="bg-white">
                          <tr>
                            <th>Entry ID</th>
                            <th>Date</th>
                            <th>Receipt</th>
                            <th>Challan no.</th>
                            <th>Amount deposited</th>
                            <th>Posted to ledger</th>
                            <th>Bank</th>
                            <th>Bench</th>
                            <th>Late fee</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.ScrollEntries || []).length === 0 ? (
                            <tr>
                              <td colSpan="10" className="text-center text-muted">
                                No scroll rows
                              </td>
                            </tr>
                          ) : (
                            detail.ScrollEntries.map((row, idx) => (
                              <tr
                                key={row.Entry_Id || idx}
                                className={
                                  row.CountsTowardDashboardSum ? "" : "table-light"
                                }
                              >
                                <td>{row.Entry_Id}</td>
                                <td>{row.Dated}</td>
                                <td>{row.Receipt_No}</td>
                                <td>{row.Challan_No || "—"}</td>
                                <td>{Number(row.Amount_Deposited).toFixed(2)}</td>
                                <td>{row.Posted_In_Ledger ? "Yes" : "No"}</td>
                                <td>{row.Bank_Name}</td>
                                <td>{row.BenchNumber}</td>
                                <td>{row.LateFeeFine ? "Yes" : "No"}</td>
                                <td>{row.Created_Date}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="card mb-3">
                  <div className="card-body">
                    <h4 className="card-title mb-3">Challans (fee vouchers)</h4>
                    <p className="small text-muted mb-3">
                      <code>tblChalanHead</code> for this student where fee <strong>Month</strong> and <strong>Year</strong>{" "}
                      match the selection (same period as the dashboard month filter).
                    </p>
                    <div className="table-responsive">
                      <table className="table table-theme table-sm">
                        <thead className="bg-white">
                          <tr>
                            <th>ID</th>
                            <th>Challan no.</th>
                            <th>Month / year</th>
                            <th>Dated</th>
                            <th>Due date</th>
                            <th>Due amount</th>
                            <th>Paid</th>
                            <th>Active</th>
                            <th>Deleted</th>
                            <th>Wing / class / section</th>
                            <th>Updated</th>
                            <th>Delete reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.ChallanHeads || []).length === 0 ? (
                            <tr>
                              <td colSpan="12" className="text-center text-muted">
                                No challan heads
                              </td>
                            </tr>
                          ) : (
                            detail.ChallanHeads.map((row, idx) => (
                              <tr
                                key={row.ID || idx}
                                className={row.Is_Deleted ? "table-warning" : ""}
                              >
                                <td>{row.ID}</td>
                                <td>{row.Challan_No || "—"}</td>
                                <td>
                                  {row.Month}/{row.Year}
                                </td>
                                <td>{row.Dated}</td>
                                <td>{row.Due_Date || "—"}</td>
                                <td>{Number(row.Due_Date_Amount).toFixed(2)}</td>
                                <td>{row.Is_Paid ? "Yes" : "No"}</td>
                                <td>{row.Is_Active ? "Yes" : "No"}</td>
                                <td>{row.Is_Deleted ? "Yes" : "No"}</td>
                                <td className="text-nowrap small">
                                  {[row.Wing_Name, row.Class_Name, row.Section_Name]
                                    .filter(Boolean)
                                    .join(" · ") || "—"}
                                </td>
                                <td className="small">{row.Updated_Date || "—"}</td>
                                <td className="small text-break">
                                  {row.DeleteReason || "—"}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="card mb-4">
                  <div className="card-body">
                    <h4 className="card-title mb-3">Ledger lines (fee vouchers / receipts)</h4>
                    <p className="small text-muted mb-3">
                      <code>tblADLedger</code> rows with <strong>PostingDate</strong> in the selected month. Highlighted rows
                      have <code>ReceiveableFee</code> not equal to <code>ReceivedAmount</code> (possible posting issue).
                    </p>
                    <div className="table-responsive">
                      <table className="table table-theme table-sm">
                        <thead className="bg-white">
                          <tr>
                            <th>ID</th>
                            <th>Posting date</th>
                            <th>Fee code</th>
                            <th>Description</th>
                            <th>Challan</th>
                            <th>Receivable</th>
                            <th>Received</th>
                            <th>Pending</th>
                            <th>Received date</th>
                            <th>Receipt ref</th>
                            <th>Bench</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detail.LedgerLines || []).length === 0 ? (
                            <tr>
                              <td colSpan="11" className="text-center text-muted">
                                No ledger rows
                              </td>
                            </tr>
                          ) : (
                            detail.LedgerLines.map((row, idx) => (
                              <tr
                                key={row.ID || idx}
                                className={ledgerMismatch(row) ? "table-danger" : ""}
                                title={
                                  ledgerMismatch(row)
                                    ? "ReceiveableFee differs from ReceivedAmount"
                                    : undefined
                                }
                              >
                                <td>{row.ID}</td>
                                <td>{row.PostingDate}</td>
                                <td>{row.FeeCode}</td>
                                <td>{row.Description}</td>
                                <td>{row.Challan_No}</td>
                                <td>{Number(row.ReceiveableFee).toFixed(2)}</td>
                                <td>{Number(row.ReceivedAmount).toFixed(2)}</td>
                                <td>{Number(row.PendingFee).toFixed(2)}</td>
                                <td>{row.ReceivedDate || "—"}</td>
                                <td>{row.ReceiptRef ?? "—"}</td>
                                <td>{row.Bench_Number ?? "—"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ScrollDifferenceDetail;
